import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { getContentAccessLogs, getContentAccessLogUsers, verifyContentAccessIntegrity } from "../api/adminApi";
import { Hero, Dropdown } from "../components/adminSurface/AdminSurface";
import { StackArt } from "../components/adminSurface/heroArt";

const PAGE_SIZE = 100;
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_ROWS = 100000;

// ── helpers ──────────────────────────────────────────────────────────────────

function toDateStr(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtAbs(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch { return String(raw); }
}

function fmtRel(raw, t) {
  if (!raw) return "";
  try {
    const diff = Date.now() - new Date(raw).getTime();
    if (diff < 60000) return t("logs.justNow");
    if (diff < 3600000) return t("logs.minutesAgo", { n: Math.floor(diff / 60000) });
    if (diff < 86400000) return t("logs.hoursAgo", { n: Math.floor(diff / 3600000) });
    if (diff < 7 * 86400000) return t("logs.daysAgo", { n: Math.floor(diff / 86400000) });
    return fmtAbs(raw).slice(0, 10);
  } catch { return fmtAbs(raw); }
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/[\s@.]+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function fmtIp(ip) {
  if (!ip) return null;
  if (ip === "::1" || ip === "127.0.0.1" || ip === "::ffff:127.0.0.1") return "localhost";
  return ip;
}

function shortKey(k) {
  if (!k) return "";
  return k.length > 12 ? `${k.slice(0, 12)}…` : k;
}

function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(items) {
  const header = ["Date", "User", "Action", "Kind", "ArchiveNo", "ContentKey", "Database", "Organization", "IP"];
  const rows = items.map((r) => [
    fmtAbs(r.occurredAt), r.userName ?? "", r.action ?? "", r.kind ?? "",
    r.archiveNo ?? "", r.contentKey ?? "", r.dbName ?? "", r.organization ?? "", r.clientIp ?? "",
  ]);
  const csv = [header, ...rows].map((row) => row.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `content-access-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Content-access action palette (write path emits View/Download; Print/Export reserved).
const ACTION_META = {
  View:     { color: "#1d4ed8", bg: "#dbeafe" },
  Download: { color: "#7c3aed", bg: "#ede9fe" },
  Print:    { color: "#b45309", bg: "#fef3c7" },
  Export:   { color: "#15803d", bg: "#dcfce7" },
};

function actionMeta(action) {
  if (!action) return { color: "#6b7280", bg: "#f3f4f6" };
  return ACTION_META[action] || { color: "#374151", bg: "#f9fafb" };
}

const KINDS = ["Document", "Media", "Transcript", "Record"];

const AVATAR_COLORS = ["#3b82f6","#8b5cf6","#ec4899","#f59e0b","#10b981","#06b6d4","#6366f1"];
function avatarColor(name) {
  if (!name) return "#9ca3af";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

const RANGES = [
  { label: "1h",   getFrom: () => toDateStr(new Date(Date.now() - 3600000)) },
  { label: "24h",  getFrom: () => toDateStr(new Date(Date.now() - 86400000)) },
  { label: "7d",   getFrom: () => toDateStr(new Date(Date.now() - 7 * 86400000)) },
  { label: "30d",  getFrom: () => toDateStr(new Date(Date.now() - 30 * 86400000)) },
  { label: "All",  getFrom: () => "" },
];

// ── component ─────────────────────────────────────────────────────────────────

export default function ContentAccessLogs() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // { ok, ... } | { error }

  // filter inputs
  const [search, setSearch]               = useState("");
  const [userFilter, setUserFilter]       = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userOpen, setUserOpen]           = useState(false);
  const userRef                           = useRef(null);
  const [actionFilter, setActionFilter]   = useState([]);
  const [actionOpen, setActionOpen]       = useState(false);
  const actionRef                         = useRef(null);
  const [kindFilter, setKindFilter]       = useState("");
  const [fromDate, setFromDate]           = useState(() => toDateStr(new Date(Date.now() - 86400000)));
  const [toDate, setToDate]               = useState("");
  const [activeRange, setActiveRange]     = useState("24h");

  const [applied, setApplied] = useState(() => ({
    fromDate: toDateStr(new Date(Date.now() - 86400000)),
  }));

  useEffect(() => {
    if (!token) return;
    getContentAccessLogUsers(token).then(setUserSuggestions).catch(() => {});
  }, [token]);

  useEffect(() => {
    function handleClick(e) {
      if (actionRef.current && !actionRef.current.contains(e.target)) setActionOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const load = useCallback(async (pageIndex, filters) => {
    setLoading(true);
    setError("");
    try {
      const result = await getContentAccessLogs(token, {
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
        search:   filters.search      || undefined,
        userName: filters.userFilter  || undefined,
        action:   filters.actionFilter?.length ? filters.actionFilter.join(",") : undefined,
        kind:     filters.kindFilter  || undefined,
        from:     filters.fromDate    || undefined,
        to:       filters.toDate      || undefined,
      });
      setItems(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (e) {
      setError(e.message || t("logs.loadFailedContent"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(page, applied); }, [page, applied, load]);

  const applyFilters = () => {
    setPage(0);
    setActiveRange("");
    setApplied({ search, userFilter, actionFilter, kindFilter, fromDate, toDate });
  };

  const applyRange = (range) => {
    const from = range.getFrom();
    setFromDate(from);
    setToDate("");
    setActiveRange(range.label);
    setPage(0);
    setApplied({ search, userFilter, actionFilter, kindFilter, fromDate: from, toDate: "" });
  };

  const resetFilters = () => {
    setSearch(""); setUserFilter(""); setActionFilter([]); setKindFilter("");
    setFromDate(""); setToDate(""); setPage(0); setActiveRange("All");
    setApplied({});
  };

  const toggleAction = (a) => {
    setActionFilter(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  };

  const verifyIntegrity = async () => {
    if (verifying) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await verifyContentAccessIntegrity(token);
      setVerifyResult(r);
    } catch (e) {
      setVerifyResult({ error: e?.message || String(e) });
    } finally {
      setVerifying(false);
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const filterArgs = {
        search:   applied.search                       || undefined,
        userName: applied.userFilter                   || undefined,
        action:   applied.actionFilter?.length ? applied.actionFilter.join(",") : undefined,
        kind:     applied.kindFilter                   || undefined,
        from:     applied.fromDate                     || undefined,
        to:       applied.toDate                       || undefined,
      };
      const all = [];
      let skip = 0;
      let serverTotal = Infinity;
      let capped = false;
      while (true) {
        const result = await getContentAccessLogs(token, { ...filterArgs, skip, take: EXPORT_PAGE_SIZE });
        const batch = result.items ?? [];
        all.push(...batch);
        serverTotal = result.total ?? all.length;
        if (batch.length < EXPORT_PAGE_SIZE) break;
        if (all.length >= serverTotal) break;
        if (all.length >= EXPORT_MAX_ROWS) { capped = true; break; }
        skip += EXPORT_PAGE_SIZE;
      }
      if (all.length === 0) {
        alert(t("logs.exportNone"));
        return;
      }
      exportCsv(all);
      if (capped) {
        alert(t("logs.exportCapped", { max: EXPORT_MAX_ROWS.toLocaleString(), total: serverTotal.toLocaleString() }));
      }
    } catch (e) {
      alert(t("logs.exportFailed", { error: e?.message || e }));
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = page * PAGE_SIZE + 1;
  const rangeEnd   = Math.min((page + 1) * PAGE_SIZE, total);

  const filterPills = [
    applied.search      && { key: "search",   label: `"${applied.search}"`, clear: () => { setSearch(""); setApplied(a => ({ ...a, search: "" })); } },
    applied.userFilter  && { key: "user",     label: t("logs.filterUser", { value: applied.userFilter }), clear: () => { setUserFilter(""); setApplied(a => ({ ...a, userFilter: "" })); } },
    applied.actionFilter?.length && { key: "action", label: t("logs.filterAction", { value: applied.actionFilter.join(", ") }), clear: () => { setActionFilter([]); setApplied(a => ({ ...a, actionFilter: [] })); } },
    applied.kindFilter  && { key: "kind",     label: t("logs.filterKind", { value: applied.kindFilter }), clear: () => { setKindFilter(""); setApplied(a => ({ ...a, kindFilter: "" })); } },
    applied.fromDate    && { key: "from",     label: t("logs.filterFrom", { value: applied.fromDate }), clear: () => { setFromDate(""); setActiveRange(""); setApplied(a => ({ ...a, fromDate: "" })); } },
    applied.toDate      && { key: "to",       label: t("logs.filterTo", { value: applied.toDate }), clear: () => { setToDate(""); setApplied(a => ({ ...a, toDate: "" })); } },
  ].filter(Boolean);

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container" style={{ paddingBottom: 40 }}>

        {/* ── Page header ── */}
        <Hero
          title={t("logs.contentTitle")}
          subtitle={t("logs.contentSubtitle")}
          logo={<StackArt />}
          actions={
            <>
              <button className="asf-hero__btn" onClick={verifyIntegrity} disabled={verifying}>
                {verifying ? t("logs.verifying") : `🛡 ${t("logs.verifyBtn")}`}
              </button>
              <button className="asf-hero__btn" onClick={handleExport} disabled={items.length === 0 || exporting || loading}>
                {exporting ? t("logs.exporting") : `↓ ${t("logs.exportCsv")}`}
              </button>
            </>
          }
        />

        {/* ── Stats strip ── */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { label: t("logs.totalMatched"), value: loading ? "…" : total.toLocaleString() },
            { label: t("logs.showing"), value: loading ? "…" : items.length > 0 ? `${rangeStart}–${rangeEnd}` : "0" },
            { label: t("logs.page"), value: t("logs.pageOf", { page: page + 1, total: totalPages }) },
          ].map((s) => (
            <div key={s.label} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "8px 14px", minWidth: 100 }}>
              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginTop: 2 }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* ── Filter bar ── */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, marginRight: 4 }}>{t("logs.range")}:</span>
            {RANGES.map((r) => (
              <button key={r.label} onClick={() => applyRange(r)}
                style={{
                  padding: "4px 12px", borderRadius: 9999, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1.5px solid",
                  borderColor: activeRange === r.label ? "#7c3aed" : "#e5e7eb",
                  background: activeRange === r.label ? "#7c3aed" : "#fff",
                  color: activeRange === r.label ? "#fff" : "#374151",
                  transition: "all .12s ease",
                }}>
                {r.label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: "2 1 180px" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t("logs.search")}</label>
              <input type="text" placeholder={t("logs.searchPlaceholderContent")} value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                className="asf-input" style={{ fontSize: 13 }} />
            </div>
            <div ref={userRef} style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 130px", position: "relative" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t("logs.user")}</label>
              <input type="text" placeholder={t("logs.userPlaceholder")} value={userFilter} autoComplete="off"
                onChange={(e) => { setUserFilter(e.target.value); setUserOpen(true); }}
                onFocus={() => setUserOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { setUserOpen(false); applyFilters(); }
                  if (e.key === "Escape") setUserOpen(false);
                }}
                className="asf-input" style={{ fontSize: 13 }} />
              {userOpen && (() => {
                const q = userFilter.trim().toLowerCase();
                const matches = userSuggestions.filter(u => !q || u.toLowerCase().includes(q));
                if (matches.length === 0) return null;
                return (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, marginTop: 2,
                    background: "#fff", border: "1px solid #d1d5db", borderRadius: 10,
                    boxShadow: "0 4px 16px rgba(0,0,0,.10)", maxHeight: 220, overflowY: "auto",
                  }}>
                    {matches.map((u) => (
                      <div key={u} onMouseDown={(e) => { e.preventDefault(); setUserFilter(u); setUserOpen(false); }}
                        style={{
                          display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
                          cursor: "pointer", fontSize: 13,
                          background: u === userFilter ? "#f6f2ff" : "transparent",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f6f2ff"}
                        onMouseLeave={e => e.currentTarget.style.background = u === userFilter ? "#f6f2ff" : "transparent"}>
                        <div style={{
                          width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                          background: avatarColor(u), color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9, fontWeight: 800,
                        }}>
                          {initials(u)}
                        </div>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div ref={actionRef} style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 150px", position: "relative" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t("logs.action")}</label>
              <button type="button" className="asf-input" onClick={() => setActionOpen(o => !o)}
                style={{
                  fontSize: 13, textAlign: "left", cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "space-between", gap: 6,
                }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: actionFilter.length ? "#111827" : "#9ca3af" }}>
                  {actionFilter.length === 0 ? t("logs.anyAction") : actionFilter.join(", ")}
                </span>
                <span style={{ fontSize: 10, color: "#9ca3af", flexShrink: 0 }}>{actionOpen ? "▲" : "▼"}</span>
              </button>
              {actionOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, marginTop: 2,
                  background: "#fff", border: "1px solid #d1d5db", borderRadius: 10,
                  boxShadow: "0 4px 16px rgba(0,0,0,.10)", padding: "6px 0", minWidth: 160,
                }}>
                  {Object.entries(ACTION_META).map(([key, meta]) => {
                    const checked = actionFilter.includes(key);
                    return (
                      <label key={key} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "7px 14px",
                        cursor: "pointer", userSelect: "none",
                        background: checked ? "#f6f2ff" : "transparent",
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleAction(key)}
                          style={{ accentColor: meta.color, width: 14, height: 14, flexShrink: 0 }} />
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 9999,
                          fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                          background: meta.bg, color: meta.color,
                        }}>{key}</span>
                      </label>
                    );
                  })}
                  {actionFilter.length > 0 && (
                    <div style={{ borderTop: "1px solid #f3f4f6", padding: "6px 14px 2px" }}>
                      <button type="button" onClick={() => setActionFilter([])}
                        style={{ fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                        {t("logs.clearSelection")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: "1 1 120px" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t("logs.kind")}</label>
              <Dropdown
                value={kindFilter}
                onChange={setKindFilter}
                placeholder={t("logs.anyKind")}
                options={[{ value: "", label: t("logs.anyKind") }, ...KINDS.map((k) => ({ value: k, label: k }))]}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: "0 0 auto" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t("logs.from")}</label>
              <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setActiveRange(""); }}
                className="asf-input" style={{ fontSize: 13 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: "0 0 auto" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t("logs.to")}</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="asf-input" style={{ fontSize: 13 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, justifyContent: "flex-end" }}>
              <label style={{ fontSize: 11, visibility: "hidden" }}>.</label>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="asf-cta" onClick={applyFilters} style={{ fontSize: 13, padding: "9px 18px" }}>{t("logs.searchBtn")}</button>
                <button onClick={resetFilters} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#6d28d9" }}>{t("logs.reset")}</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Active filter pills ── */}
        {filterPills.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "#6b7280", alignSelf: "center" }}>{t("logs.activeFilters")}</span>
            {filterPills.map((p) => (
              <span key={p.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f4f2fb", color: "#6d28d9", border: "1px solid #e9d5ff", borderRadius: 9999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                {p.label}
                <button onClick={p.clear} style={{ border: "none", background: "transparent", color: "#6d28d9", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 16px", marginBottom: 12, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>⚠</span> {error}
          </div>
        )}

        {/* ── Integrity check result ── */}
        {verifyResult && (() => {
          const good = verifyResult.error == null && verifyResult.ok === true;
          const msg = verifyResult.error != null
            ? t("logs.verifyError", { error: verifyResult.error })
            : verifyResult.ok
              ? t("logs.verifyOk", { count: (verifyResult.checked ?? 0).toLocaleString() })
              : t("logs.verifyFail", { id: verifyResult.firstBreakId, reason: verifyResult.reason });
          return (
            <div style={{
              color: good ? "#15803d" : "#b91c1c",
              background: good ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${good ? "#bbf7d0" : "#fecaca"}`,
              borderRadius: 10, padding: "10px 16px", marginBottom: 12, fontSize: 13,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>{good ? "🛡" : "⚠"}</span> {msg}
              <button onClick={() => setVerifyResult(null)}
                style={{ marginLeft: "auto", border: "none", background: "transparent", color: "inherit", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          );
        })()}

        {/* ── Table ── */}
        <div className="logs-card" style={{ overflowX: "auto", borderRadius: 12 }}>
          {items.length === 0 && !loading ? (
            <div style={{ padding: "60px 40px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>{t("logs.noContentTitle")}</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{t("logs.noEntriesHint")}</div>
            </div>
          ) : (
            <table className="logs-table" style={{ minWidth: 880 }}>
              <thead>
                <tr>
                  <th style={{ width: 130, paddingLeft: 16 }}>{t("logs.colWhen")}</th>
                  <th style={{ width: 170 }}>{t("logs.colUser")}</th>
                  <th style={{ width: 110 }}>{t("logs.colAction")}</th>
                  <th style={{ width: 110 }}>{t("logs.colKind")}</th>
                  <th>{t("logs.colContent")}</th>
                  <th style={{ width: 130 }}>{t("logs.colDatabase")}</th>
                  <th style={{ width: 120 }}>{t("logs.colIp")}</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>
                        {[130, 170, 110, 110, 0, 130, 120].map((w, j) => (
                          <td key={j} style={j === 0 ? { paddingLeft: 16 } : {}}>
                            <div className="skeleton-line" style={{ width: j === 4 ? "85%" : "65%", height: 10, margin: 0 }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : items.map((row) => {
                      const meta = actionMeta(row.action);
                      const isExpanded = expandedId === row.id;
                      return (
                        <React.Fragment key={row.id}>
                          <tr onClick={() => setExpandedId(isExpanded ? null : row.id)}
                            style={{ cursor: "pointer", background: isExpanded ? "#f6f2ff" : undefined }}>
                            {/* When */}
                            <td style={{ paddingLeft: 16, whiteSpace: "nowrap" }}>
                              <span title={fmtAbs(row.occurredAt)} style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>
                                {fmtRel(row.occurredAt, t)}
                              </span>
                              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                                {fmtAbs(row.occurredAt).slice(0, 10)}
                              </div>
                            </td>

                            {/* User */}
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{
                                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                  background: avatarColor(row.userName), color: "#fff",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 10, fontWeight: 800,
                                }}>
                                  {initials(row.userName)}
                                </div>
                                <span style={{ fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }} title={row.userName ?? ""}>
                                  {row.userName || <span style={{ color: "#9ca3af" }}>{t("logs.unknown")}</span>}
                                </span>
                              </div>
                            </td>

                            {/* Action */}
                            <td>
                              {row.action
                                ? <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 9999, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", background: meta.bg, color: meta.color, whiteSpace: "nowrap" }}>
                                    {row.action}
                                  </span>
                                : <span style={{ color: "#9ca3af" }}>—</span>}
                            </td>

                            {/* Kind */}
                            <td style={{ fontSize: 12, color: "#6b7280" }}>
                              {row.kind
                                ? <span style={{ background: "#f3f4f6", border: "1px solid #e5e7eb", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 500 }}>{row.kind}</span>
                                : <span style={{ color: "#d1d5db" }}>—</span>}
                            </td>

                            {/* Content (ArchiveNo + short ContentKey) */}
                            <td style={{ fontSize: 13, color: "#374151", maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.contentKey ?? ""}>
                              {row.archiveNo
                                ? <span style={{ fontWeight: 500 }}>{row.archiveNo}</span>
                                : <span style={{ color: "#9ca3af" }}>—</span>}
                              {row.contentKey && (
                                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>{shortKey(row.contentKey)}</span>
                              )}
                            </td>

                            {/* Database */}
                            <td style={{ fontSize: 12, color: "#6b7280" }}>
                              {row.dbName
                                ? <span style={{ background: "#f3f4f6", border: "1px solid #e5e7eb", padding: "2px 7px", borderRadius: 6, fontSize: 11, whiteSpace: "nowrap", fontWeight: 500 }}>{row.dbName}</span>
                                : <span style={{ color: "#d1d5db" }}>—</span>}
                            </td>

                            {/* IP */}
                            <td style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", fontFamily: "ui-monospace, monospace" }}>
                              {fmtIp(row.clientIp) || <span style={{ color: "#d1d5db" }}>—</span>}
                            </td>
                          </tr>

                          {/* Expanded detail row */}
                          {isExpanded && (
                            <tr style={{ background: "#faf7ff" }}>
                              <td colSpan={7} style={{ padding: "12px 16px 14px 56px" }}>
                                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
                                  {[
                                    { label: t("logs.fullTimestamp"), value: fmtAbs(row.occurredAt) },
                                    { label: t("logs.organization"), value: row.organization },
                                    { label: t("logs.ipAddress"), value: fmtIp(row.clientIp) },
                                    { label: t("logs.colDatabase"), value: row.dbName },
                                    { label: t("logs.kind"), value: row.kind },
                                    { label: t("logs.archiveNo"), value: row.archiveNo },
                                    { label: t("logs.contentKey"), value: row.contentKey, mono: true },
                                  ].filter((f) => f.value).map((f) => (
                                    <div key={f.label}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{f.label}</div>
                                      <div style={{ color: "#111827", fontFamily: f.mono ? "ui-monospace, monospace" : undefined, fontSize: f.mono ? 12 : undefined, wordBreak: f.mono ? "break-all" : undefined }}>{f.value}</div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination ── */}
        {total > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              {loading ? t("logs.loading") : t("logs.showingRecords", { start: rangeStart, end: rangeEnd, total: total.toLocaleString() })}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="icon-btn" onClick={() => setPage(0)} disabled={page === 0} style={{ padding: "4px 10px", fontSize: 13 }}>«</button>
              <button className="icon-btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: "4px 12px", fontSize: 13 }}>‹ {t("logs.prev")}</button>
              <span style={{ display: "flex", alignItems: "center", padding: "4px 12px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#374151" }}>
                {page + 1} / {totalPages}
              </span>
              <button className="icon-btn" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: "4px 12px", fontSize: 13 }}>{t("logs.next")} ›</button>
              <button className="icon-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} style={{ padding: "4px 10px", fontSize: 13 }}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
